import { lazy, Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Route, Routes, Navigate, useLocation, useParams } from "react-router-dom";

import { Loader } from "@dexibit/core";

import ErrorFallback from "./components/ErrorFallback";
import { Navigation } from "./components/Navigation";
import { OutageBanner } from "./components/OutageBanner";
import PageLoadingSkeleton from "./components/PageLoadingSkeleton";
import {
  useAppListeners,
  useDebouncedError,
  useInit,
  useIntercom,
  useModules,
  usePendo,
  useUserContext,
} from "./hooks";
import useLaunchDarkly from "./hooks/useLaunchDarkly";
import FourOhFourPage from "./pages/FourOhFourPage";
import UnauthorizedPage from "./pages/UnauthorizedPage";
import IFramePage from "./pages/IFramePage";
import {
  AppGrid,
  AppContent,
  LoadingContainer,
  ErrorContainer,
  MegaHeaderArea,
  NavigationArea,
  MainContentArea,
} from "./styles";
import logger from "./utils/logger";
import { MEGA_ADMIN_ACCOUNT_ID, MegaModeHeader } from "./components/MegaModeHeader";
import { getSublinkRoutes } from "./utils/url";
import { NavigationLink } from "./types";
import { RequireAuth } from "./features/data-explorer/components/AuthenticatedRoute";
import { UserRole } from "@de-common/types/UserRole";

const AskPage = lazy(() => import("./pages/AskPage"));
const ChatLayout = lazy(() =>
  import("./pages/AskPage/layouts/ChatLayout").then((m) => ({
    default: m.ChatLayout,
  })),
);
const WelcomeChat = lazy(() =>
  import("./pages/AskPage/WelcomeChat/index").then((m) => ({
    default: m.WelcomeChat,
  })),
);
const ConversationGuard = lazy(() =>
  import("./pages/AskPage/ConversationGuard").then((m) => ({
    default: m.ConversationGuard,
  })),
);
const ChatMissingPage = lazy(() =>
  import("./pages/AskPage/ChatMissingPage").then((m) => ({
    default: m.ChatMissingPage,
  })),
);
const KnowledgePage = lazy(() =>
  import("./pages/AskPage/KnowledgePage/KnowledgePage").then((m) => ({
    default: m.KnowledgePage,
  })),
);
const ForecastsPage = lazy(() =>
  import("./pages/AskPage/ForecastsPage/ForecastsPage").then((m) => ({
    default: m.ForecastsPage,
  })),
);
const AutomationsPage = lazy(() =>
  import("./pages/AutomationsPage").then((m) => ({
    default: m.AutomationsPage,
  })),
);
const AutomationDetail = lazy(() =>
  import("./pages/AutomationsPage/AutomationDetail").then((m) => ({
    default: m.AutomationDetail,
  })),
);
const ApiPage = lazy(() => import("./pages/ApiPage"));
const AlmanacPage = lazy(() => import("./pages/AlmanacPage"));
const DataExplorerPage = lazy(() => import("./pages/DataExplorerPage"));
const ClientsPage = lazy(() =>
  import("./pages/ApiPage/ClientsPage").then((m) => ({
    default: m.ClientsPage,
  })),
);
const QueryHistoryPage = lazy(() =>
  import("./pages/ApiPage/QueryHistoryPage").then((m) => ({
    default: m.QueryHistoryPage,
  })),
);

const NATIVE_ROUTE_KEYS = [
  "/v2/ask",
  "/v2/data/api",
  "/v2/almanac",
  "/v2/data/data-explorer",
];

const LegacyRedirectPaths = ["/v2", "/v2/home", "/v2/app"];
const LegacyRedirect = ({ links }: { links: NavigationLink[] }) => {
  return links[0] ? <Navigate to={links[0]?.to} replace /> : <FourOhFourPage />;
};

const HomeToDashboardRedirect = () => {
  const { "*": rest } = useParams();
  const path = rest?.replace(/^dashboard\/?/, "") || "";
  return <Navigate to={`/v2/dashboard/${path}`} replace />;
};

const App = () => {
  const { authenticated, userInfo, isInitError, isInitLoading, isUninitialized, config } =
    useInit();
  const { user, account, venue, isUserContextError, isUserContextLoading } =
    useUserContext();
  const { links, activeLink, isModulesError, hasLinks } = useModules(user, account);
  const location = useLocation();

  const hasMegaModeHeader = account?.id === MEGA_ADMIN_ACCOUNT_ID;

  const gridClassName = hasMegaModeHeader ? "has-mega-header" : "";

  useLaunchDarkly(user);

  useIntercom(user, venue, account);
  usePendo(user, venue);

  const { dialogVisible } = useAppListeners(user, config);

  const appIsLoading =
    isUninitialized ||
    isInitLoading ||
    isUserContextLoading ||
    !authenticated ||
    !hasLinks;
  const appHasError = isInitError || isModulesError || isUserContextError;

  // Debounce error display to prevent flashing during initial load
  const shouldShowError = useDebouncedError(appHasError, appIsLoading);

  if (shouldShowError) {
    return (
      <AppGrid className={gridClassName}>
        <ErrorContainer>
          <ErrorFallback />
        </ErrorContainer>
      </AppGrid>
    );
  }

  if (appIsLoading) {
    return (
      <AppGrid className={gridClassName}>
        <LoadingContainer>
          <Loader size="large" />
        </LoadingContainer>
      </AppGrid>
    );
  }

  return (
    <AppGrid className={gridClassName}>
      {hasMegaModeHeader && (
        <MegaHeaderArea>
          <MegaModeHeader />
        </MegaHeaderArea>
      )}
      <NavigationArea>
        <Navigation
          links={links}
          activeLink={activeLink}
          dialogVisible={dialogVisible}
          userInfo={userInfo}
          accountName={account?.name}
          venueName={venue?.name}
        />
      </NavigationArea>
      <MainContentArea>
        <OutageBanner />
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onError={(error: Error) => logger.error("Application error", error)}
          resetKeys={[location.pathname]}
        >
          <AppContent>
            <Routes>
              {LegacyRedirectPaths.map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={<LegacyRedirect links={links} />}
                />
              ))}
              {/* Native routes for DE */}
              <Route path="/v2/ask" element={<Navigate to="/v2/ask/rex" replace />} />
              <Route
                path="/v2/ask/rex"
                element={
                  <Suspense fallback={<PageLoadingSkeleton />}>
                    <AskPage />
                  </Suspense>
                }
              >
                <Route element={<ChatLayout />}>
                  <Route index element={<WelcomeChat />} />
                  <Route path="knowledge" element={<KnowledgePage />} />
                  <Route path="forecasts" element={<ForecastsPage />} />
                  <Route path="automations" element={<AutomationsPage />} />
                  <Route
                    path="automations/:automationId"
                    element={<AutomationDetail />}
                  />
                  <Route path=":conversationId" element={<ConversationGuard />} />
                  <Route path="404" element={<ChatMissingPage />} />
                </Route>
              </Route>
              <Route
                path="/v2/data/api"
                element={
                  <RequireAuth
                    requiredRoles={[UserRole.ACCOUNT_ADMIN, UserRole.SUPPORT]}
                    roleRequirement="any"
                  >
                    <Suspense fallback={<PageLoadingSkeleton />}>
                      <ApiPage />
                    </Suspense>
                  </RequireAuth>
                }
              >
                <Route index element={<Navigate to="clients" replace />} />
                <Route path="clients" element={<ClientsPage />} />
                <Route path="query-history" element={<QueryHistoryPage />} />
              </Route>
              <Route
                path="/v2/almanac"
                element={
                  <Suspense fallback={<PageLoadingSkeleton />}>
                    <AlmanacPage />
                  </Suspense>
                }
              />
              <Route
                path="/v2/data/data-explorer/*"
                element={
                  <Suspense fallback={<PageLoadingSkeleton />}>
                    <DataExplorerPage />
                  </Suspense>
                }
              />
              <Route path="/v2/unauthorized" element={<UnauthorizedPage />} />
              {/* Iframe routes (excluding native routes) */}
              {getSublinkRoutes(links)
                .filter(
                  (route) => !NATIVE_ROUTE_KEYS.some((key) => route.key.startsWith(key)),
                )
                .map((route) => (
                  <Route
                    key={route.key}
                    path={route.path}
                    element={
                      route.redirectTo ? (
                        <Navigate to={route.redirectTo} replace />
                      ) : (
                        <IFramePage basePath={route.basePath} iframe={route.iframe!} />
                      )
                    }
                  />
                ))}
              <Route path="/v2/home/*" element={<HomeToDashboardRedirect />} />
              <Route path="*" element={<FourOhFourPage />} />
            </Routes>
          </AppContent>
        </ErrorBoundary>
      </MainContentArea>
    </AppGrid>
  );
};

export default App;
